import { useTranslation } from 'react-i18next';
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useAppDispatch, useAppSelector } from '../../../../../reduxService/hooks';
import { conversionLabel } from '../../../../conversion';
import { selectProjectInfo } from '../../../../store/projectInfo/projectInfoSlice';
import { selectResults } from '../../../selector/conversion';
import './DischargeCurve.scss';
import { roundToTwo } from '../../InputForm/Geometry/utils';
import React from 'react';
import { useCurrentPng } from 'recharts-to-png';
import { setDischargeCurveImg } from '../../../store/project/resultsSlice';

interface IDischargeCurveProps {
    withSave?: boolean;
}

interface ICustomTooltip {
    active?: boolean;
    payload?: any;
}

interface ICustomDot {
    cx?: number;
    cy?: number;
    value?: number;
}

const DischargeCurve = (props: IDischargeCurveProps) => {
    const { t } = useTranslation();

    const results = useAppSelector(selectResults);
    const projectInfo = useAppSelector(selectProjectInfo);
    const dispatch = useAppDispatch();
    let data = results.data?.dischargeResult.map(value => ({
        x: roundToTwo(value.discharge),
        y: roundToTwo(value.waterDepth),
    }));
    const [getAreaPng, { ref: areaRef, isLoading }] = useCurrentPng();

    const CustomTooltip = (props: ICustomTooltip) => {
        if (props.active && props.payload && props.payload.length) {
            return (
                <div className='discharge-curve-tooltip'>
                    <span>{`${t('tab_results_discharge')}: ${props.payload[0].payload.x} ${conversionLabel.m3s_ft3s[projectInfo.data?.unitOfMeasure as number]}`}</span>
                    <span>{`${t('tab_results_depth')}: ${props.payload[0].payload.y} ${conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number]}`}</span>
                </div>
            );
        }
        return null;
    };

    const CustomDot = (props: ICustomDot) => {
        const { cx, cy, value } = props;
        if (value! == roundToTwo(results.data?.generalResult.general.waterDepth)) {
            return (
                <svg x={cx! - 10} y={cy! - 10} width={20} height={20} fill='#004652'>
                    <circle cx='10' cy='10' r='5' />
                </svg>
            );
        }
        return <svg></svg>;
    };

    const handleAreaDownload = React.useCallback(async () => {
        const png = await getAreaPng();

        if (png) {
            dispatch(setDischargeCurveImg(png));
        }
    }, [getAreaPng]);

    React.useEffect(() => {
        if (data && data.length > 0 && props.withSave) handleAreaDownload();
    });

    return (
        <div className={'d-flex w-100 h-85 align-items-center justify-content-center'}>
            {!!data && data.length > 0 && (
                <ResponsiveContainer width='70%' height='70%'>
                    <LineChart
                        id='currentChart'
                        width={500}
                        height={300}
                        ref={areaRef}
                        margin={{
                            top: 5,
                            right: 30,
                            left: 20,
                            bottom: 5,
                        }}>
                        <CartesianGrid strokeDasharray='3 3' />
                        <YAxis
                            dataKey='y'
                            domain={['auto', 'auto']}
                            type='number'
                            interval={0}
                            label={{
                                value: t('general_results_water', { unit_measure: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number] }),
                                style: { textAnchor: 'middle' },
                                angle: -90,
                                position: 'left',
                                offset: -17,
                            }}
                            allowDataOverflow={true}
                        />
                        <XAxis
                            dataKey='x'
                            domain={['auto', 'auto']}
                            interval={0}
                            type='number'
                            label={{
                                key: 'xAxisLabel',
                                value: t('channel_item_discharge', { unit_measure: conversionLabel.m3s_ft3s[projectInfo.data?.unitOfMeasure as number] }),
                                position: 'bottom',
                                offset: -7,
                            }}
                            allowDataOverflow={true}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Line dot={<CustomDot />} strokeWidth={2} data={data} type='monotone' dataKey='y' stroke='#004652' tooltipType='none' />
                        <ReferenceLine
                            stroke='#004652'
                            strokeDasharray='3 3'
                            segment={[
                                { x: results.data?.generalResult.general.discharge, y: results.data?.generalResult.general.waterDepth },
                                { x: results.data?.generalResult.general.discharge, y: 0 },
                            ]}
                        />
                        <ReferenceLine
                            stroke='#004652'
                            strokeDasharray='3 3'
                            segment={[
                                { x: results.data?.generalResult.general.discharge, y: results.data?.generalResult.general.waterDepth },
                                { x: 0, y: results.data?.generalResult.general.waterDepth },
                            ]}
                        />
                    </LineChart>
                </ResponsiveContainer>
            )}
        </div>
    );
};

export default DischargeCurve;
